import { Cell, Grid, HeadingSection, TabItem, Tabs, VFlow } from 'bold-ui'
import CheckPermission from 'components/auth/CheckPermission'
import useSession from 'components/auth/useSession'
import { RichTextField } from 'components/form/final-form/RichTextField'
import { TipoAtendimentoProfissional } from 'graphql/types.generated'
import React, { useState } from 'react'
import { FormSpy } from 'react-final-form'
import Permissions from 'types/Permissions'
import { MetaPath } from 'util/metaPath/metaPath'
import { AtendimentoProfissional } from 'view/atendimentos/types/AtendimentoProfissionalModel'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import { grupoCboAtestado, grupoCboEncaminhamentoExterno, grupoCboOrientacoes, grupoCboPrescricao } from './acessos'
import AtestadoView, { AtestadoPlanoModel } from './atestados/AtestadosView'
import { CiapPlanoField, CiapPlanoModel } from './components/CiapPlanoField'
import { ProcedimentoPlanoField, ProcedimentoPlanoModel } from './components/ProcedimentoPlanoField'
import { DeclaracaoComparecimentoFormView } from './declaracao-comparecimento/DeclaracaoComparecimentoFormView'
import EncaminhamentoExternoView from './encaminhamento-externo/components/EncaminhamentoExternoView'
import { EncaminhamentoExternoPlanoModel } from './encaminhamento-externo/model'
import EvolucoesOdontologicasView, { OdontologiaModel } from './evolucoes-odontologicas/EvolucoesOdontologicasView'
import { OrientacoesView } from './orientacoes/OrientacoesView'
import { OrientacaoFormModel } from './orientacoes/types/OrientacoesModel'
import PrescricaoMedicamentoView, {
  PrescricaoMedicamentoPlanoModel,
} from './prescricao-medicamento/PrescricaoMedicamentoView'

export interface PlanoFormModel {
  texto?: string
  ciaps?: CiapPlanoModel[]
  procedimentos?: ProcedimentoPlanoModel[]
  atestado?: AtestadoPlanoModel
  prescricaoMedicamento?: PrescricaoMedicamentoPlanoModel
  odontologia?: OdontologiaModel
  encaminhamentoExterno?: EncaminhamentoExternoPlanoModel
  orientacao?: OrientacaoFormModel[]
}

export interface PlanoFormProps {
  name: MetaPath<PlanoFormModel>
  cidadao: CidadaoAtendimento
  prontuarioId: ID
  tipoAtendProf: TipoAtendimentoProfissional
  isAtendimentoOdonto?: boolean
  atendimentoId: ID
  atendimentoProfissional: AtendimentoProfissional
}

enum PlanoTabs {
  PRESCRICAO_MEDICAMENTOS,
  ATESTADO,
  ENCAMINHAMENTO_EXTERNO,
  DECLARACAO_COMPARECIMENTO,
  ORIENTACOES,
}

export const PlanoForm = (props: PlanoFormProps) => {
  const { name, cidadao, prontuarioId, isAtendimentoOdonto, atendimentoId, atendimentoProfissional } = props

  const { hasAuthorization, hasCboAuth } = useSession({ fetchPolicy: 'cache-only' })

  const hasAutorizationAtestado = hasCboAuth(grupoCboAtestado.visualizar)
  const hasAuthorizationEncaminhamentoExterno = hasCboAuth(grupoCboEncaminhamentoExterno)
  const hasAutorizationDeclaracaoTab =
    !hasAutorizationAtestado &&
    hasAuthorization(Permissions.visualizarListaDeAtendimento.gerarDeclaracaoDeComparecimento)
  const hasAutorizationPrescricao = hasCboAuth(grupoCboPrescricao.visualizar)

  const [currentPlanoTab, setCurrentPlanoTab] = useState(PlanoTabs.PRESCRICAO_MEDICAMENTOS)

  return (
    <>
      {!isAtendimentoOdonto && (
        <RichTextField
          name={name.texto}
          maxLength={4000}
          placeholder='Insira informações adicionais sobre o plano de cuidado.'
        />
      )}

      <VFlow>
        <HeadingSection level={3} title='Intervenções e/ou procedimentos clínicos realizados' vSpace={0}>
          <Grid>
            {!isAtendimentoOdonto && (
              <Cell size={12}>
                <CiapPlanoField name={name.ciaps} cidadao={cidadao} />
              </Cell>
            )}
            <Cell size={12}>
              <ProcedimentoPlanoField
                name={name.procedimentos}
                cidadao={cidadao}
                filtrarProcedimentosOdonto={isAtendimentoOdonto}
              />
            </Cell>
          </Grid>
        </HeadingSection>

        {isAtendimentoOdonto && (
          <EvolucoesOdontologicasView
            name={name.odontologia}
            prontuarioId={prontuarioId}
            cidadaoDataNascimento={cidadao.dataNascimento}
          />
        )}

        <Grid>
          <Cell size={12}>
            <Tabs>
              {hasAutorizationPrescricao && (
                <TabItem
                  defaultChecked
                  active={currentPlanoTab === PlanoTabs.PRESCRICAO_MEDICAMENTOS}
                  onClick={() => {
                    setCurrentPlanoTab(PlanoTabs.PRESCRICAO_MEDICAMENTOS)
                  }}
                >
                  Prescrição de medicamentos
                </TabItem>
              )}
              {hasAutorizationAtestado && (
                <TabItem
                  active={currentPlanoTab === PlanoTabs.ATESTADO}
                  onClick={() => setCurrentPlanoTab(PlanoTabs.ATESTADO)}
                >
                  Atestados
                </TabItem>
              )}
              <CheckPermission permission={grupoCboOrientacoes.visualizar}>
                <TabItem
                  active={currentPlanoTab === PlanoTabs.ORIENTACOES}
                  onClick={() => setCurrentPlanoTab(PlanoTabs.ORIENTACOES)}
                >
                  Orientações
                </TabItem>
              </CheckPermission>

              {hasAuthorizationEncaminhamentoExterno && (
                <TabItem
                  active={currentPlanoTab === PlanoTabs.ENCAMINHAMENTO_EXTERNO}
                  onClick={() => setCurrentPlanoTab(PlanoTabs.ENCAMINHAMENTO_EXTERNO)}
                >
                  Encaminhamentos
                </TabItem>
              )}
              {hasAutorizationDeclaracaoTab && (
                <TabItem
                  active={currentPlanoTab === PlanoTabs.DECLARACAO_COMPARECIMENTO}
                  onClick={() => setCurrentPlanoTab(PlanoTabs.DECLARACAO_COMPARECIMENTO)}
                >
                  Declaração de comparecimento
                </TabItem>
              )}
            </Tabs>
          </Cell>
          <Cell size={12}>
            <FormSpy subscription={{ values: true, pristine: true }}>
              {(soap) => (
                <>
                  {currentPlanoTab === PlanoTabs.ATESTADO && hasAutorizationAtestado && (
                    <AtestadoView
                      name={name.atestado}
                      atendimentoId={atendimentoId}
                      prontuarioId={prontuarioId}
                      cidadao={cidadao}
                      atendimentoProfissional={atendimentoProfissional}
                    />
                  )}
                  {currentPlanoTab === PlanoTabs.ORIENTACOES && (
                    <CheckPermission permission={grupoCboOrientacoes.visualizar}>
                      <OrientacoesView
                        prontuarioId={prontuarioId}
                        name={name.orientacao}
                        orientacoes={soap.values?.plano?.orientacao}
                        atendimentoProfissional={atendimentoProfissional}
                      />
                    </CheckPermission>
                  )}
                  {currentPlanoTab === PlanoTabs.ENCAMINHAMENTO_EXTERNO && hasAuthorizationEncaminhamentoExterno && (
                    <EncaminhamentoExternoView
                      name={name.encaminhamentoExterno}
                      prontuarioId={prontuarioId}
                      cidadaoSexo={cidadao.sexo}
                      isAtendimentoOdonto={isAtendimentoOdonto}
                      cboAtendProf={atendimentoProfissional.lotacao.cbo.cbo2002}
                      atendimentoProfissional={atendimentoProfissional}
                    />
                  )}
                  {currentPlanoTab === PlanoTabs.DECLARACAO_COMPARECIMENTO && hasAutorizationDeclaracaoTab && (
                    <DeclaracaoComparecimentoFormView atendId={atendimentoId} />
                  )}
                  {currentPlanoTab === PlanoTabs.PRESCRICAO_MEDICAMENTOS && hasAutorizationPrescricao && (
                    <PrescricaoMedicamentoView
                      name={name.prescricaoMedicamento}
                      prontuarioId={prontuarioId}
                      atendimentoId={atendimentoId}
                      medicamentos={soap.values?.plano?.prescricaoMedicamento?.medicamentos}
                      cidadaoDataNascimento={cidadao.dataNascimento}
                      cidadaoSexo={cidadao.sexo}
                    />
                  )}
                </>
              )}
            </FormSpy>
          </Cell>
        </Grid>
      </VFlow>
    </>
  )
}
